import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import type { AxiosError, AxiosResponse } from '@fs/zion-axios'
import {
  updatePlaylist as updatePlaylistApi,
  addMultipleItemsToPlaylist,
  removePlaylistItem,
  updatePlaylistItemsOrder,
} from '../../api'
import notifySentry from '../../lib/helpers/sentryNotifier'
import type { Playlist, PlaylistsByItem } from '../../api/rootstech/playlist/playlist.types'

type PlaylistsCache = { data: { results: Playlist[] } }
type PlaylistsByItemsCache = { data: PlaylistsByItem }

export type UpdatePlaylistParams = {
  id: string
  name?: string
  description?: string
  addItems?: string[]
  removeItems?: string[]
  orderItems?: string[]
}

type UsePlaylistUpdate = Omit<UseMutationResult<void, AxiosError, UpdatePlaylistParams>, 'mutateAsync'> & {
  updatePlaylist: (params: UpdatePlaylistParams) => Promise<void>
}

const useUpdatePlaylistDetails = (): UseMutationResult<
  void,
  AxiosError,
  { playlistId: string; name?: string; description?: string }
> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; name?: string; description?: string }>({
    mutationFn: ({ playlistId, name, description }) => {
      const cache = queryClient.getQueryData<PlaylistsCache>(['playlists'])
      const existing = cache?.data.results.find((p) => p.id === playlistId)
      return updatePlaylistApi({
        playlistId,
        data: {
          name: name ?? existing?.name ?? '',
          description: description ?? existing?.description ?? '',
        },
      })
    },
    onSuccess: (_, { playlistId, name, description }) => {
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.map((p) =>
              p.id === playlistId
                ? { ...p, ...(name !== undefined && { name }), ...(description !== undefined && { description }) }
                : p
            ),
          },
        }
      })
      showStatusOverlay({
        message: t('playlists.alerts.details-updated', 'Playlist details updated.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.details.update-error',
          "We're sorry, but there was an issue updating the playlist details. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

const addPlaylistToSession = (
  sessionData: PlaylistsByItem,
  sessionIds: string[],
  playlist: Playlist,
  playlistId: string
): PlaylistsByItem => {
  const updatedData = { ...sessionData }

  sessionIds.forEach((sessionId) => {
    const currentSession = updatedData[sessionId] ?? { results: [] }
    const hasPlaylist = currentSession.results.some((p) => p.id === playlistId)

    if (!hasPlaylist) {
      updatedData[sessionId] = { results: [...currentSession.results, playlist] }
    }
  })

  return updatedData
}

const removePlaylistFromSession = (
  sessionData: PlaylistsByItem,
  sessionId: string,
  playlistId: string
): PlaylistsByItem => {
  if (!sessionData[sessionId]) return sessionData

  return {
    ...sessionData,
    [sessionId]: { results: sessionData[sessionId].results.filter((p) => p.id !== playlistId) },
  }
}

const updatePlaylistSize = (
  cache: PlaylistsCache | undefined,
  playlistId: string,
  sizeDelta: number
): PlaylistsCache | undefined => {
  if (!cache) return cache

  return {
    ...cache,
    data: {
      ...cache.data,
      results: cache.data.results.map((p) =>
        p.id === playlistId ? { ...p, size: Math.max(0, (p.size ?? 0) + sizeDelta) } : p
      ),
    },
  }
}

const useAddPlaylistItems = (): UseMutationResult<void, AxiosError, { playlistId: string; sessionIds: string[] }> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; sessionIds: string[] }>({
    mutationFn: ({ playlistId, sessionIds }) => addMultipleItemsToPlaylist({ playlistId, sessionIds }),
    onSuccess: (_, { playlistId, sessionIds }) => {
      const now = new Date().toISOString()

      // Update playlistItems cache to add the new items
      let addedCount = 0
      queryClient.setQueryData<AxiosResponse<{ id: string; addedAt: string }[]>>(
        ['playlistItems', playlistId],
        (old) => {
          if (!old?.data || !Array.isArray(old.data)) return old
          const existingIds = new Set(old.data.map((item: { id: string; addedAt: string }) => item.id))
          const newItems = sessionIds.filter((id) => !existingIds.has(id)).map((id) => ({ id, addedAt: now }))
          addedCount = newItems.length
          return { ...old, data: [...old.data, ...newItems] }
        }
      )

      // Update playlistsByItems cache to add playlist to each session
      const playlistsCache = queryClient.getQueryData<PlaylistsCache>(['playlists'])
      const playlist = playlistsCache?.data.results.find((p) => p.id === playlistId)
      if (!playlist) return

      queryClient.setQueriesData<PlaylistsByItemsCache>({ queryKey: ['playlistsByItems'] }, (old) => {
        if (!old) return old
        return { ...old, data: addPlaylistToSession(old.data, sessionIds, playlist, playlistId) }
      })

      // Update playlists cache to reflect the new size
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => updatePlaylistSize(old, playlistId, addedCount))

      showStatusOverlay({
        message: t('playlists.alerts.item-added', 'Item added to playlist.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.items.add-error',
          "We're sorry, but there was an issue adding the item to your playlist. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

const useRemovePlaylistItem = (): UseMutationResult<void, AxiosError, { playlistId: string; sessionId: string }> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; sessionId: string }>({
    mutationFn: ({ playlistId, sessionId }) => removePlaylistItem({ playlistId, sessionId }),
    onSuccess: (_, { playlistId, sessionId }) => {
      queryClient.setQueriesData<PlaylistsByItemsCache>({ queryKey: ['playlistsByItems'] }, (old) => {
        if (!old) return old
        return { ...old, data: removePlaylistFromSession(old.data, sessionId, playlistId) }
      })

      // Update playlistItems cache to remove the item
      queryClient.setQueryData<AxiosResponse<{ id: string; addedAt: string }[]>>(
        ['playlistItems', playlistId],
        (old) => {
          if (!old?.data || !Array.isArray(old.data)) return old
          return { ...old, data: old.data.filter((item: { id: string; addedAt: string }) => item.id !== sessionId) }
        }
      )

      // Update playlists cache to reflect the new size
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => updatePlaylistSize(old, playlistId, -1))

      showStatusOverlay({
        message: t('playlists.alerts.item-removed', 'Item removed from playlist.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.items.remove-error',
          "We're sorry, but there was an issue removing the item from your playlist. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

const useReorderPlaylistItems = (): UseMutationResult<
  void,
  AxiosError,
  { playlistId: string; orderedIds: string[] }
> => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, { playlistId: string; orderedIds: string[] }>({
    mutationFn: ({ playlistId, orderedIds }) =>
      updatePlaylistItemsOrder({ playlistId, ordering: { method: 'custom', ids: orderedIds } }),
    onSuccess: (_, { playlistId, orderedIds }) => {
      // Update playlistItems cache to match the new order
      queryClient.setQueryData<AxiosResponse<{ id: string; addedAt: string }[]>>(
        ['playlistItems', playlistId],
        (old) => {
          if (!old?.data || !Array.isArray(old.data)) return old
          const itemsMap = new Map(old.data.map((item: { id: string; addedAt: string }) => [item.id, item]))
          const reorderedData = orderedIds
            .map((id) => itemsMap.get(id))
            .filter((item): item is { id: string; addedAt: string } => item !== undefined)
          return { ...old, data: reorderedData }
        }
      )

      showStatusOverlay({
        message: t('playlists.alerts.items-reordered', 'Playlist order updated.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.items.reorder-error',
          "We're sorry, but there was an issue reordering the playlist items. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })
}

export const usePlaylistUpdate = (): UsePlaylistUpdate => {
  const updateDetailsMutation = useUpdatePlaylistDetails()
  const addItemsMutation = useAddPlaylistItems()
  const removeItemMutation = useRemovePlaylistItem()
  const reorderItemsMutation = useReorderPlaylistItems()

  const { mutateAsync: updatePlaylist, ...rest } = useMutation<void, AxiosError, UpdatePlaylistParams>({
    mutationFn: async ({ id: playlistId, name, description, addItems, removeItems, orderItems }) => {
      const ops: Promise<void>[] = []

      if (name !== undefined || description !== undefined) {
        ops.push(updateDetailsMutation.mutateAsync({ playlistId, name, description }))
      }
      if (addItems?.length) {
        ops.push(addItemsMutation.mutateAsync({ playlistId, sessionIds: addItems }))
      }
      if (removeItems?.length) {
        ops.push(...removeItems.map((sessionId) => removeItemMutation.mutateAsync({ playlistId, sessionId })))
      }
      if (orderItems?.length) {
        ops.push(reorderItemsMutation.mutateAsync({ playlistId, orderedIds: orderItems }))
      }

      await Promise.all(ops)
    },
  })

  return { updatePlaylist, ...rest }
}
